"use client";

import React from "react";
import { Car, DollarSign, Phone, CircleCheck, CircleAlert, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CITIES = ["Abbotsford", "Burnaby", "Chilliwack", "Coquitlam", "Delta", "Langley", "Maple Ridge", "Mission", "New Westminster", "North Vancouver", "Pitt Meadows", "Port Coquitlam", "Port Moody", "Richmond", "Surrey", "Vancouver", "White Rock", "West Vancouver", "Whistler", "Squamish", "Pemberton"];
const CONDITIONS = ["Running", "Non-running", "Damaged", "Other"];
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Quote Request Received!</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/50 hover:bg-primary/10">
          Request Another Quote
        </Button>
      </div>;
  }
  const nativeInputClasses = "flex w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/40 aria-invalid:border-destructive disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      {}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 rounded-xl bg-background/40 border border-white/5 shadow-[inset_0_0_20px_rgba(132,255,0,0.02)]">
        <FormField control={control} name="vehicle_year" rules={{
        required: "Year is required",
        min: {
          value: 1900,
          message: "Must be after 1900"
        },
        max: {
          value: 2025,
          message: "Must be 2025 or earlier"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Vehicle Year</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g. 2015" className="bg-background/60 border-white/10 focus-visible:ring-primary/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="vehicle_make" rules={{
        required: "Make is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Vehicle Make</FormLabel>
              <FormControl>
                <Input type="text" placeholder="e.g. Toyota" className="bg-background/60 border-white/10 focus-visible:ring-primary/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="vehicle_model" rules={{
        required: "Model is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Vehicle Model</FormLabel>
              <FormControl>
                <Input type="text" placeholder="e.g. Camry" className="bg-background/60 border-white/10 focus-visible:ring-primary/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone is required",
        pattern: {
          value: /^[0-9\-+() ]+$/,
          message: "Invalid phone format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input type="tel" placeholder="778-555-1234" className="pl-9 bg-background/60 border-white/10 focus-visible:ring-primary/50" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input type="text" placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="location_city" rules={{
        required: "Pickup city is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Pickup City</FormLabel>
              <FormControl>
                <select className={`${nativeInputClasses} h-9 appearance-none`} {...field}>
                  <option value="" disabled>Select your city</option>
                  {CITIES.map(city => <option key={city} value={city} className="bg-background text-foreground">
                      {city}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="vehicle_condition" rules={{
        required: "Condition is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel>Vehicle Condition</FormLabel>
              <FormControl>
                <div className="flex flex-wrap gap-4 pt-1">
                  {CONDITIONS.map(condition => <label key={condition} className="flex items-center gap-2 text-sm cursor-pointer group">
                      <div className="relative flex items-center justify-center">
                        <input type="radio" value={condition} checked={field.value === condition} onChange={field.onChange} className="peer sr-only" />
                        <div className="size-4 rounded-full border border-input bg-transparent peer-checked:border-primary peer-checked:bg-primary/20 transition-all"></div>
                        <div className="absolute size-2 rounded-full bg-primary opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                      </div>
                      <span className="group-hover:text-primary transition-colors">{condition}</span>
                    </label>)}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} />
              </FormControl>
              <FormDescription>We'll send the quote to this email if you prefer.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem>
              <FormLabel>Additional Details <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <textarea className={`${nativeInputClasses} min-h-[80px] py-2 resize-y`} placeholder="Any extra info about the vehicle or pickup location..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 flex items-start gap-3 text-destructive">
          <CircleAlert className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <div className="pt-4 flex justify-center">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto min-w-[280px] h-14 text-lg font-bold tracking-wide shadow-[0_0_20px_rgba(132,255,0,0.25)] hover:shadow-[0_0_30px_rgba(132,255,0,0.4)] transition-all duration-300 animate-[pulse_3s_ease-in-out_infinite] hover:animate-none">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing...
            </> : <>
              <DollarSign className="mr-2 size-5" />
              Get Instant Quote Now
            </>}
        </Button>
      </div>
    </Form>;
}
export default function GetYourInstantQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="get-your-instant-quote" className="dark bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-primary/5 rounded-full blur-[150px] translate-y-1/3 pointer-events-none" />
      
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="text-center mb-10 space-y-4">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-2">
            <Car className="size-8 text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-serif">
            Cash for Junk Cars in BC
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Fill out the form below for a free, no-obligation quote. We offer same-day pickup and instant cash across British Columbia.
          </p>
        </div>

        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="bg-card/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 lg:p-10 shadow-2xl relative overflow-hidden">
            {}
            <div className="absolute inset-0 shadow-[inset_0_0_40px_rgba(132,255,0,0.03)] pointer-events-none rounded-2xl" />
            
            <FormProvider formId="instant_quote_form_home" sectionName="Get Your Instant Quote" defaultValues={{
            name: "",
            phone: "",
            email: "",
            vehicle_year: "",
            vehicle_make: "",
            vehicle_model: "",
            location_city: "",
            vehicle_condition: "Running",
            additional_details: ""
          }} validationRules={{
            name: {
              required: true
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            email: {
              pattern: "email"
            },
            vehicle_year: {
              required: true,
              min: 1900,
              max: 2025
            },
            vehicle_make: {
              required: true
            },
            vehicle_model: {
              required: true
            },
            location_city: {
              required: true
            },
            vehicle_condition: {
              required: true
            }
          }} submitText="Get Instant Quote Now" successMessage="Thank you! Your quote request has been submitted successfully. Our team will contact you shortly with your cash offer.">
              <QuoteForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}